import React, { useState } from 'react';
import axios from 'axios';
import { baseURL } from '../base_url';
import { PuffLoader } from 'react-spinners';
import { Button, Table } from '@mui/material';
import { GiCheckMark } from 'react-icons/gi';
import './eligibility.css';
import * as XLSX from 'xlsx';

const StellantisCheck = () => {
    const [loading, setLoading] = useState(false);
    const [responseData, setResponseData] = useState([]);

    const [totalVehicles, setTotalVehicles] = useState(0);
    const [eligibleCount, setEligibleCount] = useState(0);
    const [notEligibleCount, setNotEligibleCount] = useState(0);
    const [errorCount, setErrorCount] = useState(0);

    const [hasChecked, setHasChecked] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [exporting, setExporting] = useState(false);

    const ITEMS_PER_PAGE = 30;

    // ---------------------------------------------------------
    // Transform API response for display
    // ---------------------------------------------------------
    const transformResponse = (data) => {
        if (!data || typeof data !== 'object') {
            return {};
        }

        const transformed = {};

        for (const [key, value] of Object.entries(data)) {
            if (Array.isArray(value) && value.length === 0) {
                transformed[key] = 'N/A';
            } else if (value === '') {
                transformed[key] = 'N/A';
            } else if (
                typeof value === 'object' &&
                value !== null
            ) {
                transformed[key] =
                    Object.keys(value).length === 0
                        ? 'N/A'
                        : JSON.stringify(value, null, 2);
            } else {
                transformed[key] = value;
            }
        }

        // Rename availableRemoteCommands
        if (
            transformed['availableRemoteCommands'] !==
            undefined
        ) {
            transformed['Remote Commands'] =
                transformed['availableRemoteCommands'];

            delete transformed['availableRemoteCommands'];
        }

        // Rename offers
        if (transformed['offers'] !== undefined) {
            transformed['Package'] = transformed['offers'];

            delete transformed['offers'];
        }

        return transformed;
    };

    // ---------------------------------------------------------
    // Check eligibility
    // ---------------------------------------------------------
    const handleCheckEligibility = async (page = 1) => {
        setLoading(true);

        try {
            const res = await axios.post(
                `${baseURL}/check_vin_eligibiliy_for_all`,
                {
                    page: page,
                    limit: ITEMS_PER_PAGE
                }
            );

            const data = res.data || {};

            const results = data.results || [];

            // -------------------------------------------------
            // Update pagination
            // -------------------------------------------------
            setCurrentPage(data.page || page);
            setTotalPages(data.total_pages || 0);

            // Total number of vehicles in the fleet
            setTotalVehicles(data.total || 0);

            // Current page results
            setResponseData(results);

            // -------------------------------------------------
            // Calculate current page counts
            // -------------------------------------------------
            let eligible = 0;
            let notEligible = 0;
            let errors = 0;

            results.forEach((vehicle) => {
                if (vehicle.error) {
                    errors++;
                } else if (
                    vehicle.eligibility?.eligible === true
                ) {
                    eligible++;
                } else if (
                    vehicle.eligibility?.eligible === false
                ) {
                    notEligible++;
                } else {
                    errors++;
                }
            });

            setEligibleCount(eligible);
            setNotEligibleCount(notEligible);
            setErrorCount(errors);

            setHasChecked(true);

        } catch (error) {
            console.error(
                'Vehicle eligibility check failed:',
                error
            );

            setResponseData([]);

            setTotalVehicles(0);
            setEligibleCount(0);
            setNotEligibleCount(0);
            setErrorCount(0);

            setCurrentPage(1);
            setTotalPages(0);

            alert(
                error?.response?.data?.message ||
                'Failed to check vehicle eligibility.'
            );
        } finally {
            setLoading(false);
        }
    };

    // ---------------------------------------------------------
    // Render eligibility response
    // ---------------------------------------------------------
    const renderEligibilityResponse = (vehicle) => {
        if (vehicle.error) {
            return (
                <div
                    style={{
                        color: '#ff0000',
                        whiteSpace: 'pre-wrap'
                    }}
                >
                    {vehicle.error}
                </div>
            );
        }

        if (!vehicle.eligibility) {
            return 'N/A';
        }

        const transformedData = transformResponse(
            vehicle.eligibility
        );

        return (
            <div className="eligibility-response">
                {Object.entries(transformedData).map(
                    ([key, value]) => (
                        <div
                            key={key}
                            className="eligibility-response-row"
                        >
                            <div className="eligibility-response-key">
                                <b>{key}</b>
                            </div>

                            <div className="eligibility-response-value">
                                {typeof value === 'string' &&
                                (
                                    value.startsWith('{') ||
                                    value.startsWith('[')
                                ) ? (
                                    <pre>{value}</pre>
                                ) : (
                                    value?.toString() || 'N/A'
                                )}
                            </div>
                        </div>
                    )
                )}
            </div>
        );
    };

    // ---------------------------------------------------------
    // Render eligibility status
    // ---------------------------------------------------------
    const renderStatus = (vehicle) => {
        if (vehicle.error) {
            return (
                <span
                    style={{
                        color: '#ff0000',
                        fontWeight: 'bold'
                    }}
                >
                    Error
                </span>
            );
        }

        if (vehicle.eligibility?.eligible === true) {
            return (
                <span
                    style={{
                        color: '#008000',
                        fontWeight: 'bold'
                    }}
                >
                    Eligible
                </span>
            );
        }

        if (vehicle.eligibility?.eligible === false) {
            return (
                <span
                    style={{
                        color: '#ff0000',
                        fontWeight: 'bold'
                    }}
                >
                    Not Eligible
                </span>
            );
        }

        return (
            <span
                style={{
                    color: '#777',
                    fontWeight: 'bold'
                }}
            >
                N/A
            </span>
        );
    };

    // ---------------------------------------------------------
    // Calculate row number
    // ---------------------------------------------------------
    const getRowNumber = (index) => {
        return (
            (currentPage - 1) * ITEMS_PER_PAGE +
            index +
            1
        );
    };

    const handleExportExcel = async () => {
        // setLoading(true);
        setExporting(true);
        try {
            // First request to know total pages
            const firstResponse = await axios.post(
                `${baseURL}/check_vin_eligibiliy_for_all`,
                {
                    page: 1,
                    limit: ITEMS_PER_PAGE
                }
            );

            const firstData = firstResponse.data || {};

            const totalPages = firstData.total_pages || 0;

            let allResults = firstData.results || [];

            // Fetch remaining pages
            for (let page = 2; page <= totalPages; page++) {
                const response = await axios.post(
                    `${baseURL}/check_vin_eligibiliy_for_all`,
                    {
                        page: page,
                        limit: ITEMS_PER_PAGE
                    }
                );

                const data = response.data || {};

                const results = data.results || [];

                allResults = [
                    ...allResults,
                    ...results
                ];
            }

            // Prepare Excel data
            const excelData = allResults.map(
                (vehicle, index) => {

                    let status = 'N/A';

                    if (vehicle.error) {
                        status = 'Error';
                    } else if (
                        vehicle.eligibility?.eligible === true
                    ) {
                        status = 'Eligible';
                    } else if (
                        vehicle.eligibility?.eligible === false
                    ) {
                        status = 'Not Eligible';
                    }

                    return {
                        '#': index + 1,
                        'VIN': vehicle.vin || '',
                        'Make': vehicle.make || '',
                        'Year': vehicle.year || '',
                        'VIN Status': vehicle.status || '',
                        'Elibility Status': status,
                        // 'Latitude': vehicle.latitude || '',
                        // 'Longitude': vehicle.longitude || '',
                        'Address': vehicle.Address || '',
                        'API Response': vehicle.API_Response || ''
                    };
                }
            );

            // Create worksheet
            const worksheet =
                XLSX.utils.json_to_sheet(excelData);

            // Create workbook
            const workbook =
                XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                'Vehicle Eligibility'
            );

            // Download Excel file
            XLSX.writeFile(
                workbook,
                'Vehicle_Eligibility.xlsx'
            );

            console.log(
                `Exported ${allResults.length} vehicles`
            );

        } catch (error) {
            console.error(
                'Excel export failed:',
                error
            );

            alert(
                error?.response?.data?.message ||
                'Failed to export vehicle eligibility data.'
            );
        } finally {
            // setLoading(false);
            setExporting(false);
        }
    };

    return (
        <div className="body_class">

            {/* -------------------------------------------------
                Loading Overlay
            ------------------------------------------------- */}
            {loading && (
                <div className="loader-overlay">
                    <div className="loader">

                        <PuffLoader
                            color="#00308F"
                            loading={true}
                            size={200}
                        />

                        <div
                            style={{
                                marginTop: '20px',
                                color: '#00308F',
                                fontWeight: 'bold',
                                textAlign: 'center'
                            }}
                        >
                            Checking vehicle eligibility...
                        </div>

                    </div>
                </div>
            )}

            <div className="container-fluid">

                {/* -------------------------------------------------
                    Page Heading
                ------------------------------------------------- */}
                <h4>
                    Check Vehicle Eligibility
                </h4>

                <div
                    style={{
                        marginTop: '10px',
                        marginBottom: '20px',
                        color: '#555'
                    }}
                >
                    Check eligibility for all JEEP and DODGE
                    vehicles available in the fleet.
                </div>

                {/* -------------------------------------------------
                    Check Eligibility Button
                ------------------------------------------------- */}
                <div
                    style={{
                        marginBottom: '25px',
                        display: 'flex',
                        gap: '10px'
                    }}
                >
                    <Button
                        variant="contained"
                        type="button"
                        onClick={() => handleCheckEligibility(1)}
                        disabled={loading}
                    >
                        <GiCheckMark />
                        &nbsp;
                        {loading ? 'Checking...' : 'Check Eligibility'}
                    </Button>

                    {hasChecked && (
                        <Button
                            variant="outlined"
                            type="button"
                            onClick={handleExportExcel}
                            disabled={loading || exporting}
                        >
                            Export Excel
                        </Button>
                    )}
                </div>

                {/* -------------------------------------------------
                    Summary
                ------------------------------------------------- */}
                {hasChecked && (
                    <div
                        className="eligibility-summary"
                        style={{
                            display: 'flex',
                            gap: '20px',
                            marginBottom: '25px',
                            flexWrap: 'wrap'
                        }}
                    >

                        {/* Total Vehicles */}
                        <div className="summary-box">
                            <div className="summary-title">
                                Total Vehicles
                            </div>

                            <div className="summary-value">
                                {totalVehicles}
                            </div>
                        </div>

                        {/* Eligible */}
                        <div className="summary-box">
                            <div className="summary-title">
                                Eligible
                            </div>

                            <div
                                className="summary-value"
                                style={{
                                    color: '#008000'
                                }}
                            >
                                {eligibleCount}
                            </div>
                        </div>

                        {/* Not Eligible */}
                        <div className="summary-box">
                            <div className="summary-title">
                                Not Eligible
                            </div>

                            <div
                                className="summary-value"
                                style={{
                                    color: '#ff0000'
                                }}
                            >
                                {notEligibleCount}
                            </div>
                        </div>

                        {/* Errors */}
                        <div className="summary-box">
                            <div className="summary-title">
                                Errors
                            </div>

                            <div
                                className="summary-value"
                                style={{
                                    color: '#ff8c00'
                                }}
                            >
                                {errorCount}
                            </div>
                        </div>

                    </div>
                )}

                {/* -------------------------------------------------
                    Results Table
                ------------------------------------------------- */}
                {hasChecked &&
                    responseData.length > 0 && (
                        <div
                            className="container-fluid"
                            style={{
                                padding: 0
                            }}
                        >

                            <div className="table-title">

                                <h5>
                                    Vehicle Eligibility Results
                                </h5>

                                <div
                                    style={{
                                        overflowX: 'auto',
                                        marginTop: '15px'
                                    }}
                                >

                                    <Table
                                        bordered
                                        hover
                                        className="eligible-response-table"
                                    >

                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>VIN</th>
                                                <th>Make</th>
                                                <th>Year</th>
                                                <th>VIN Status</th>
                                                <th>Elibility Status</th>
                                                {/*<th>Latitude</th>
                                                <th>Longitude</th>*/}
                                                <th>Address</th>
                                                <th>API Response</th>
                                                {/* <th>
                                                    Complete Response
                                                </th> */}
                                            </tr>
                                        </thead>

                                        <tbody>

                                            {responseData.map(
                                                (
                                                    vehicle,
                                                    index
                                                ) => (
                                                    <tr
                                                        key={
                                                            vehicle.vin ||
                                                            index
                                                        }
                                                    >

                                                        {/* Row Number */}
                                                        <td>
                                                            {getRowNumber(
                                                                index
                                                            )}
                                                        </td>

                                                        {/* VIN */}
                                                        <td className="key-class">
                                                            <b>
                                                                {
                                                                    vehicle.vin
                                                                }
                                                            </b>
                                                        </td>

                                                        {/* Make */}
                                                        <td>
                                                            {
                                                                vehicle.make
                                                            }
                                                        </td>

                                                        {/* Year */}
                                                        <td>
                                                            {
                                                                vehicle.year
                                                            }
                                                        </td>

                                                        {/* VIN Status */}
                                                        <td>
                                                            {
                                                                vehicle.status
                                                            }
                                                        </td>

                                                        {/* Eligibility Status */}
                                                        <td>
                                                            {renderStatus(
                                                                vehicle
                                                            )}
                                                        </td>
                                                        {/* <td>
                                                            {
                                                                vehicle.latitude
                                                            }
                                                        </td>
                                                        <td>
                                                            {
                                                                vehicle.longitude
                                                            }
                                                        </td> */}
                                                        <td>
                                                            {
                                                                vehicle.Address
                                                            }
                                                        </td>
                                                        <td>
                                                            {
                                                                vehicle.API_Response
                                                            }
                                                        </td>

                                                        {/* Complete Response */}
                                                        {/* <td>
                                                            {renderEligibilityResponse(
                                                                vehicle
                                                            )}
                                                        </td> */}

                                                    </tr>
                                                )
                                            )}

                                        </tbody>

                                    </Table>

                                </div>

                            </div>

                        </div>
                    )}

                {/* -------------------------------------------------
                    No Results
                ------------------------------------------------- */}
                {hasChecked &&
                    responseData.length === 0 &&
                    !loading && (
                        <div
                            style={{
                                padding: '30px',
                                textAlign: 'center',
                                color: '#777'
                            }}
                        >
                            No vehicle eligibility results
                            found.
                        </div>
                    )}

                {/* -------------------------------------------------
                    Pagination
                ------------------------------------------------- */}
                {hasChecked &&
                    totalPages > 0 && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '15px',
                                marginTop: '20px',
                                marginBottom: '30px'
                            }}
                        >

                            {/* Previous */}
                            <Button
                                variant="outlined"
                                disabled={
                                    loading ||
                                    currentPage === 1
                                }
                                onClick={() =>
                                    handleCheckEligibility(
                                        currentPage - 1
                                    )
                                }
                            >
                                Previous
                            </Button>

                            {/* Page Information */}
                            <span>
                                Page{' '}
                                <b>
                                    {currentPage}
                                </b>{' '}
                                of{' '}
                                <b>
                                    {totalPages}
                                </b>
                            </span>

                            {/* Next */}
                            <Button
                                variant="outlined"
                                disabled={
                                    loading ||
                                    currentPage ===
                                        totalPages
                                }
                                onClick={() =>
                                    handleCheckEligibility(
                                        currentPage + 1
                                    )
                                }
                            >
                                Next
                            </Button>

                        </div>
                    )}

            </div>
        </div>
    );
};

export default StellantisCheck;